import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const ThreadConsumption = () => {
  // State variables for Thread Consumption calculation
  const [seamLength, setSeamLength] = useState(0); // Seam length in selected units
  const [seamLengthUnit, setSeamLengthUnit] = useState("m"); // Unit for seam length
  const [numberOfSeams, setNumberOfSeams] = useState(0); // Number of seams
  const [threadFactor, setThreadFactor] = useState(0); // Thread factor (a constant depending on the fabric and stitch type)
  const [threadConsumption, setThreadConsumption] = useState(0); // Calculated thread consumption

  // Conversion factors for different units
  const unitConversion = {
    seamLength: {
      m: 1, // meters
      cm: 100, // centimeters to meters
      mm: 1000, // millimeters to meters
      yd: 1.09361, // yards to meters
      ft: 3.28084, // feet to meters
    },
  };

  // Event handlers for input changes
  const handleSeamLengthChange = (e) => setSeamLength(parseFloat(e.target.value) || 0);
  const handleSeamLengthUnitChange = (e) => setSeamLengthUnit(e.target.value);
  const handleNumberOfSeamsChange = (e) => setNumberOfSeams(parseFloat(e.target.value) || 0);
  const handleThreadFactorChange = (e) => setThreadFactor(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate Thread Consumption
  useEffect(() => {
    if (seamLength > 0 && numberOfSeams > 0 && threadFactor > 0) {
      const seamLengthInMeters = seamLength / unitConversion.seamLength[seamLengthUnit];
      const consumption = (seamLengthInMeters * numberOfSeams * threadFactor) / 1000;
      setThreadConsumption(consumption);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [seamLength, numberOfSeams, threadFactor, seamLengthUnit]); // Added seamLengthUnit to dependencies

  return (
    <Layout title="Thread Consumption Calculator" path="thread-consumption">
      <section className="section-padding" id="thread-consumption">
        <div className="container mt-5">
          <div className="row">
            {/* Calculator Section */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Thread Consumption Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Seam Length */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="seamLength" className="form-label">
                        Seam Length
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Seam Length:</strong> The total length of seams in the garment being stitched. It can be measured in different units depending on the type of fabric or garment."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="seamLength"
                        placeholder="Enter seam length"
                        value={seamLength || ""}
                        onChange={handleSeamLengthChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={seamLengthUnit}
                        onChange={handleSeamLengthUnitChange}
                      >
                        <option value="m">Meters (m)</option>
                        <option value="cm">Centimeters (cm)</option>
                        <option value="mm">Millimeters (mm)</option>
                        <option value="yd">Yards (yd)</option>
                        <option value="ft">Feet (ft)</option>
                      </select>
                    </div>

                    {/* Input for Number of Seams */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="numberOfSeams" className="form-label">
                        Number of Seams
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Number of Seams:</strong> The total number of seams in the garment, including any side seams, sleeve seams, and other stitching."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="numberOfSeams"
                        placeholder="Enter number of seams"
                        value={numberOfSeams || ""}
                        onChange={handleNumberOfSeamsChange}
                      />
                    </div>

                    {/* Input for Thread Factor */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="threadFactor" className="form-label">
                        Thread Factor
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Thread Factor:</strong> A coefficient that indicates the thread consumption rate for stitching. It depends on the type of fabric and thread used."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="threadFactor"
                        placeholder="Enter thread factor"
                        value={threadFactor || ""}
                        onChange={handleThreadFactorChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Thread Consumption:{" "}
                        <span className="fw-bold ms-3">
                          {threadConsumption === 0
                            ? "0"
                            : threadConsumption.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          Kilograms (kg)
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Thread Consumption Calculation
                </h3>

                {/* What is Thread Consumption? */}
                <h4 className="mt-3 mb-3">What is Thread Consumption?</h4>
                <p>
                  **Thread Consumption** refers to the amount of thread required for sewing a garment, calculated based on the seam length, number of seams, and thread factor. It helps in estimating the total thread required for production.
                </p>

                {/* Why Use the Thread Consumption Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Thread Consumption Calculator?</h4>
                <p>
                  This calculator helps apparel manufacturers determine how much thread they will need, ensuring efficient material procurement and reducing waste.
                </p>

                {/* Benefits of Using the Thread Consumption Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Thread Consumption Calculator</h4>
                <ul>
                  <li>
                    <strong>Cost Efficiency:</strong> Helps calculate the exact amount of thread needed, minimizing waste.
                  </li>
                  <li>
                    <strong>Optimized Material Procurement:</strong> Assists in better planning of thread purchases based on actual consumption.
                  </li>
                  <li>
                    <strong>Production Planning:</strong> Enables manufacturers to plan production schedules based on material availability.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Thread Consumption Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li><strong>Apparel Manufacturers:</strong> To calculate thread requirements for garment production.</li>
                  <li><strong>Textile Industry:</strong> To assess material usage in garment manufacturing.</li>
                  <li><strong>Garment Factories:</strong> To plan and manage production costs effectively.</li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">How does thread consumption affect garment production costs?</h5>
                <p>
                  Accurate thread consumption calculation allows manufacturers to optimize thread purchasing, reducing excess inventory and controlling production costs. This contributes to improved profitability.
                </p>

                <h5 className="mt-3 mb-2">Can this calculator be used for different types of stitching and fabrics?</h5>
                <p>
                  Yes, the thread factor can be adjusted based on different types of stitching patterns and fabric thickness, making the calculator adaptable to various garment production scenarios.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ThreadConsumption;
